/*
 * Purpose: Dry-run orchestration — test assembled workflows with fixtures
 * Last modified: 2026-06-25 | Agent
 * Completeness: 85
 */
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { WorkflowExecutor } from '@/services/executor';
import type { Workflow } from '@/stores/workflowStore';
import type { DryRunReport, DryRunStepResult, OutcomePlan } from './types';
import { assembleWorkflowFromPlan } from './workflowAssembler';
import { auditPlanReadiness } from './planReadiness';

function stepLabel(node: Workflow['nodes'][0]): string {
  return String(node.config.name || node.subtype);
}

function statusFromContext(ctx: { output: unknown; errors: string[] }): DryRunStepResult['status'] {
  if (ctx.errors.length > 0) return 'fail';
  const out = ctx.output as Record<string, unknown> | null;
  if (out?.simulated || out?.dryRun) return 'simulated';
  if (out?.skipped) return 'warn';
  return 'pass';
}

export async function runDryRun(plan: OutcomePlan): Promise<DryRunReport> {
  const workflow = assembleWorkflowFromPlan(plan);
  const blockers: string[] = [];
  const warnings: string[] = [];

  if (workflow.nodes.length === 0) {
    return {
      planId: plan.id,
      passed: false,
      stepResults: [],
      blockers: [`No ${PRODUCT_TERMS.blocks.toLowerCase()} were assembled from this plan.`],
      warnings: [],
      readyForReview: false,
      executedAt: new Date().toISOString(),
    };
  }

  const triggers = workflow.nodes.filter((n) => n.type === 'trigger');
  if (triggers.length === 0) {
    blockers.push('Workflow has no trigger node.');
  }

  for (const c of plan.connectors.filter((x) => x.status === 'needs_credentials')) {
    warnings.push(`Connector "${c.name}" will need credentials before live run.`);
  }

  const readiness = auditPlanReadiness(plan);
  if (!readiness.allRequiredReady && readiness.requiredTotal > 0) {
    warnings.push(
      `${readiness.requiredTotal - readiness.requiredReady} required credential(s) missing — complete them in ${PRODUCT_TERMS.autoBlockFlowSetup} above (dry run still simulates).`
    );
  }

  for (const g of plan.gaps) {
    warnings.push(g);
  }

  let contexts: Awaited<ReturnType<WorkflowExecutor['execute']>> = [];

  try {
    const executor = new WorkflowExecutor(workflow, {
      dryRun: true,
      triggerFixture: plan.dryRunFixture,
      skipPersistence: true,
    });
    contexts = await executor.execute();
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Dry run failed';
    blockers.push(msg);
  }

  const stepResults: DryRunStepResult[] = workflow.nodes.map((node) => {
    const ctx = contexts.find((c) => c.nodeId === node.id);
    if (!ctx) {
      return {
        stepId: String(node.config._autoBlocksStepId || node.id),
        nodeId: node.id,
        label: stepLabel(node),
        status: 'warn' as const,
        input: null,
        output: null,
        message: 'Step not reached (branch skipped or run aborted).',
      };
    }

    const status = statusFromContext(ctx);
    return {
      stepId: String(node.config._autoBlocksStepId || node.id),
      nodeId: node.id,
      label: stepLabel(node),
      status,
      input: ctx.input,
      output: ctx.output,
      message:
        status === 'simulated'
          ? 'Simulated — no external API or inbox used.'
          : status === 'fail'
            ? ctx.errors.join('; ')
            : undefined,
    };
  });

  const failed = stepResults.filter((s) => s.status === 'fail');
  const passed = failed.length === 0 && blockers.length === 0;

  return {
    planId: plan.id,
    passed,
    stepResults,
    blockers,
    warnings,
    readyForReview: passed,
    executedAt: new Date().toISOString(),
  };
}
